import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, computed, effect, inject, input, signal } from '@angular/core';
import { ImageFit, ImageFrame } from './listing-image.model';

/**
 * image in a listing box (4:3 unless the host sets --listing-image-aspect): shows the listing frame of the image
 * (cover: the frame fills the box, contain: the whole frame on a blurred copy of the image), without frame the image centered.
 * The image is positioned from its natural size and the box size, so the frame stays exact for any box size.
 */
@Component({
    selector: 'app-listing-image',
    template: `
        @if (fit() === 'contain') {
            <img class="li-backdrop" [src]="src()" alt="" aria-hidden="true">
        }
        <img class="li-image" [class.li-positioned]="!!placement()" [src]="src()" [alt]="alt()" (load)="onLoad($event)"
            [style.--li-left]="px(placement()?.left)" [style.--li-top]="px(placement()?.top)"
            [style.--li-width]="px(placement()?.width)" [style.--li-height]="px(placement()?.height)">
    `,
    //!important and the host prefix: pages style "swiper-slide img" etc. globally with !important (e.g. width: 100%, aspect-ratio),
    //which would distort the positioned image. Size and position come as css variables, so the rules here can win.
    styles: `
        :host {
            display: block;
            position: relative;
            overflow: hidden;
            width: 100%;
            aspect-ratio: var(--listing-image-aspect, 4 / 3);
        }
        :host img.li-image,
        :host img.li-backdrop {
            position: absolute !important;
            max-width: none !important;
            max-height: none !important;
            aspect-ratio: auto !important;
            margin: 0 !important;
        }
        :host img.li-image {
            inset: 0 !important;
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important; /* until the natural size is known */
        }
        :host img.li-image.li-positioned {
            inset: auto !important;
            left: var(--li-left) !important;
            top: var(--li-top) !important;
            width: var(--li-width) !important;
            height: var(--li-height) !important;
            object-fit: fill !important;
        }
        :host img.li-backdrop {
            inset: -10% !important;
            width: 120% !important;
            height: 120% !important;
            object-fit: cover !important;
            filter: blur(18px) brightness(0.85);
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListingImageComponent implements OnInit, OnDestroy {
    readonly src = input.required<string>();
    readonly frame = input<ImageFrame | null | undefined>(null);
    readonly fit = input<ImageFit | undefined>('cover');
    readonly alt = input<string>('');

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly naturalSize = signal<{ w: number; h: number } | undefined>(undefined);
    private readonly boxSize = signal<{ w: number; h: number } | undefined>(undefined);
    private resizeObserver?: ResizeObserver;

    constructor() {
        //a new image has its own natural size
        effect(() => {
            this.src();
            this.naturalSize.set(undefined);
        });
    }

    readonly placement = computed(() => {
        const img = this.naturalSize();
        const box = this.boxSize();
        if (!img || !box || !box.w || !box.h) return undefined;
        const f = this.frame() ?? { x: 0, y: 0, w: 1, h: 1 };
        const frameW = f.w * img.w;
        const frameH = f.h * img.h;
        //cover: the frame fills the box (edges may be cut if the box ratio differs), contain: the whole frame is visible
        const scale = this.fit() === 'contain' ? Math.min(box.w / frameW, box.h / frameH) : Math.max(box.w / frameW, box.h / frameH);
        const width = img.w * scale;
        const height = img.h * scale;
        //frame center in the box center
        let left = box.w / 2 - (f.x + f.w / 2) * width;
        let top = box.h / 2 - (f.y + f.h / 2) * height;
        if (this.fit() !== 'contain') {
            //never show empty space next to the image
            left = Math.min(0, Math.max(box.w - width, left));
            top = Math.min(0, Math.max(box.h - height, top));
        }
        return { left, top, width, height };
    });

    ngOnInit(): void {
        this.resizeObserver = new ResizeObserver(entries => {
            const rect = entries[0].contentRect;
            this.boxSize.set({ w: rect.width, h: rect.height });
        });
        this.resizeObserver.observe(this.host.nativeElement);
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    px(value: number | undefined): string | null {
        return value == null ? null : value + 'px';
    }

    onLoad(event: Event): void {
        const img = event.target as HTMLImageElement;
        this.naturalSize.set({ w: img.naturalWidth, h: img.naturalHeight });
    }
}
